import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

const getAuthHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

export interface EmailListItem {
  id: number;
  from: string | null;
  to: string | null;
  subject: string | null;
  dateReceived: string | null;
  isRead: boolean;
  isStarred: boolean;
  snippet: string | null;
  /** Vínculo com o cliente (email_messages.link_status); sem cliente, diz o motivo. Null = ainda não analisado. */
  linkStatus: string | null;
  contactId: number | null;
}

export interface EmailListResponse {
  items: EmailListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EmailDetail {
  id: number;
  sectorId: number;
  from: string | null;
  to: string | null;
  cc: string | null;
  bcc: string | null;
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  dateReceived: string | null;
  isRead: boolean;
  isStarred: boolean;
  folder: string;
  linkStatus: string | null;
  contactId: number | null;
  counterpartEmail: string | null;
  attachments: { id: number; filename: string | null; contentType: string | null; sizeBytes: number | null; bucketPath: string; downloadUrl?: string | null }[];
}

export interface SendEmailRequest {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  isHtml?: boolean;
  attachments?: { filename: string; contentType?: string; base64Content: string }[];
}

export interface ReplyEmailRequest {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body: string;
  isHtml?: boolean;
  attachments?: { filename: string; contentType?: string; base64Content: string }[];
}

export const listEmails = async (
  sectorId: number,
  folder: string = 'inbox',
  page: number = 1,
  pageSize: number = 20
): Promise<EmailListResponse> => {
  const response = await axios.get<EmailListResponse>(`${API_URL}/EmailMessage/list`, {
    params: { sectorId, folder, page, pageSize },
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getEmailById = async (id: number): Promise<EmailDetail> => {
  const response = await axios.get<EmailDetail>(`${API_URL}/EmailMessage/${id}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const markRead = async (id: number, isRead: boolean): Promise<void> => {
  await axios.patch(
    `${API_URL}/EmailMessage/${id}/read`,
    { isRead },
    { headers: getAuthHeaders() }
  );
};

export const toggleStar = async (id: number): Promise<{ isStarred: boolean }> => {
  const response = await axios.patch<{ isStarred: boolean }>(
    `${API_URL}/EmailMessage/${id}/star`,
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const moveEmail = async (id: number, folder: string): Promise<void> => {
  await axios.post(
    `${API_URL}/EmailMessage/${id}/move`,
    { folder },
    { headers: getAuthHeaders() }
  );
};

export const deleteEmailPermanently = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/EmailMessage/${id}`, {
    headers: getAuthHeaders(),
  });
};

export const sendEmail = async (sectorId: number, request: SendEmailRequest): Promise<void> => {
  await axios.post(
    `${API_URL}/EmailMessage/send?sectorId=${sectorId}`,
    request,
    { headers: getAuthHeaders() }
  );
};

export const replyEmail = async (
  sectorId: number,
  messageId: number,
  request: ReplyEmailRequest
): Promise<void> => {
  await axios.post(
    `${API_URL}/EmailMessage/${messageId}/reply?sectorId=${sectorId}`,
    request,
    { headers: getAuthHeaders() }
  );
};

/** Status do vínculo que uma pessoa pode corrigir à mão (espelha EmailLinkStatuses.ManuallyLinkable no backend). */
export const MANUALLY_LINKABLE_STATUSES = ['uncertain', 'ambiguous', 'no_match', 'prospecting', 'job_candidate', 'automated'];

/** Status em que o e-mail já está na conversa de um cliente. */
export const LINKED_STATUSES = ['linked', 'linked_reply', 'new_contact', 'linked_manual'];

/**
 * Liga à mão o e-mail (e os outros sem cliente do mesmo endereço) a um cliente existente (contactId) ou a um cliente
 * novo criado com o endereço do e-mail (sem contactId).
 */
export const linkEmailToContact = async (
  id: number,
  contactId?: number
): Promise<{ contactId: number; linked: number; created: boolean; linkStatus: string }> => {
  const response = await axios.post(
    `${API_URL}/EmailMessage/${id}/link`,
    { contactId: contactId ?? null },
    { headers: getAuthHeaders() }
  );
  return response.data;
};
